import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DataTable, Modal } from "../widgets";
import {
  usePermission,
  isPlannedOrTodate,
  isAutomaticLoad,
} from "../../helpers/common";
import LoadRow from "./LoadRow";
import moment from "moment-timezone";
import FilterDropdown from "./FilterDropdown";
import { useLoadHistoryContext } from "./context/LoadHistoryContext";
import { SubNav } from "../layout/submenu";
import LoadBulkEditRow from "./LoadBulkEditRow";
import {
  maxLoadCountForFilter,
  minLoadCountForFilter,
} from "./LoadHistoryFilter";
import {
  restoreDeletedLoad,
  setLoadHistoryHeader,
  setRestoreLoad,
} from "../../actions/equipment-group";
import {
  secondsToTime,
  toDisplayDateTimeString,
  toDisplayTimeString,
} from "../../helpers/moment-ex";
import {
  getUserFarmConfiguration,
  saveUserFarmConfiguration,
} from "../../actions/user";

const LoadHistoryTable = (props) => {
  const dispatch = useDispatch();
  const searchParams = useSelector((state) => state.analyze.searchParams);
  const currentFarm = useSelector((state) => state.farm.currentFarm);
  const userFarmConfig = useSelector((state) => state.user.farmConfiguration);

  const {
    rangeId,
    range,
    custom: { from, to },
  } = searchParams;
  const {
    timezone_identifier: timezone,
    load_type: loadType,
    timezone_identifier: timezoneIdentifier,
  } = currentFarm;

  const {
    farmId,
    filteredList,
    setSortList,
    loadList,
    weightShortName,
    showableCustomColumns,
    customColumns,
    nonEmptyLoadList,
    localFilterState,
    setLocalFilterState,
    loadParams,
    initialData,
  } = props;

  const {
    filter,
    handleFilterSelection,
    bulkEditLoadList,
    handleBulkEditLoadSelect,
    enableBulkEdit,
    showDeletedLoads,
  } = useLoadHistoryContext();

  let serialNumber = 0;
  const allow = usePermission();
  const [isAllowCreate, setIsAllowCreate] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [currentLoadActivityId, setCurrentLoadActivityId] = useState(false);
  const isAllowEdit = allow("load-history", "edit");
  const isAllowDelete = allow("load-history", "delete");

  const [sort, setSort] = useState({
    direction: 0,
    field: null,
  });

  const [columnOrder, setColumnOrder] = useState([]);

  const getInitialColumnOrder = useMemo(() => {
    const { isRequesting } = userFarmConfig;
    if (!isRequesting) {
      const { payload } = userFarmConfig;
      if (payload && payload.remember) {
        const { remember } = payload;
        const loadHistroy =
          remember && remember.load_histroy ? remember.load_histroy : {};
        if (loadHistroy && loadHistroy.column_order) {
          return loadHistroy.column_order;
        }
      }
    }
    return [];
  }, [userFarmConfig]);
  const filterCondition =
    nonEmptyLoadList.length > minLoadCountForFilter &&
    nonEmptyLoadList.length < maxLoadCountForFilter;

  const saveColumnOrder = (newOrder) => {
    if (getInitialColumnOrder !== newOrder && newOrder.length > 0) {
      const payload =
        userFarmConfig && userFarmConfig.payload
          ? userFarmConfig.payload
          : null;
      const remember = payload && payload.remember ? payload.remember : null;
      const loadHistroy =
        remember && remember.load_histroy ? remember.load_histroy : {};
      const addConfiguration = {
        farm_uuid: farmId,
        configuration: {
          remember: {
            ...remember,
            load_histroy: {
              ...loadHistroy,
              column_order: newOrder.filter((item) => item),
            },
          },
        },
      };
      dispatch(saveUserFarmConfiguration(addConfiguration));
      dispatch(getUserFarmConfiguration({ farmId }));
    }
  };

  const getOrderdColums = (newColmns) => {
    if (enableBulkEdit) {
      newColmns = newColmns.filter(
        (item) => item !== "action" && item !== "restore"
      );
    } else if (showDeletedLoads) {
      newColmns = newColmns.filter((item) => item !== "action");
    } else {
      newColmns = newColmns.filter((item) => item !== "restore");
    }
    return [...new Set(newColmns)];
  };

  const getTableHead = useMemo(() => {
    let newColmns =
      showableCustomColumns && showableCustomColumns.length > 0
        ? showableCustomColumns
        : customColumns;
    newColmns = getOrderdColums(newColmns);

    let orderedData =
      columnOrder && columnOrder.length > 0
        ? columnOrder
        : getInitialColumnOrder && getInitialColumnOrder.length > 0
        ? getInitialColumnOrder
        : [];

    if (orderedData && orderedData.length > 0) {
      const filterdColumns = orderedData.filter((item) =>
        newColmns.includes(item)
      );
      if (showDeletedLoads) {
        if (!filterdColumns.includes("restore")) filterdColumns.push("restore");
      }
      if (orderedData.length !== newColmns.length) {
        const addedColumns = newColmns.filter(
          (item) => !orderedData.includes(item)
        );
        newColmns = [...filterdColumns, ...addedColumns];
        newColmns = [...new Set(newColmns)];
        if (newColmns !== columnOrder) {
          if (addedColumns.length > 0) {
            addedColumns.forEach((element) => {
              if (element && getInitialColumnOrder.includes(element)) {
                const desiredPosition = getInitialColumnOrder.indexOf(element);
                const currentIndex = newColmns.indexOf(element);
                if (
                  desiredPosition >= 0 &&
                  desiredPosition < newColmns.length
                ) {
                  newColmns.splice(currentIndex, 1);
                  newColmns.splice(desiredPosition, 0, element);
                }
              }
            });
          }
          setColumnOrder(newColmns);
        }
        dispatch(setLoadHistoryHeader(newColmns));
      } else {
        /* if we uncheck the single selected item and check the unselected single item scenario */
        if (
          orderedData.length === newColmns.length &&
          orderedData !== newColmns
        ) {
          const addedColumns = newColmns.filter(
            (item) => !orderedData.includes(item)
          );
          newColmns = [...filterdColumns, ...addedColumns];
          newColmns = [...new Set(newColmns)];
        } else {
          newColmns = filterdColumns;
        }
      }
    }
    return newColmns;
  }, [showableCustomColumns, columnOrder]);

  useEffect(() => {
    setSortList(getSortedList());
  }, [filteredList, sort.direction, sort.field]);

  useEffect(() => {
    let timeDiffInMin = 0;
    if (from && to)
      timeDiffInMin = moment
        .utc(to)
        .tz(timezone)
        .diff(moment.utc(from).tz(timezone), "minutes");

    const allowCreate =
      allow("load-history", "create") &&
      timeDiffInMin <= 2 * 24 * 60 &&
      !isPlannedOrTodate(range);

    setIsAllowCreate(allowCreate);
  }, [rangeId]);

  const renderSortOrder = (sortField) => {
    const { direction, field } = sort;
    if (sortField !== field || direction === 0)
      return (
        <i
          className="fa fa-caret-down sort-direction hidden"
          aria-hidden="true"
        />
      );
    if (direction === 0)
      return (
        <i
          className="fa fa-caret-down sort-direction hidden"
          aria-hidden="true"
        />
      );
    if (direction === 1)
      return (
        <i className="fa fa-caret-down sort-direction" aria-hidden="true" />
      );
    if (direction === 2)
      return <i className="fa fa-caret-up sort-direction" aria-hidden="true" />;
  };

  const handleSort = (sortField) => {
    const { direction, field } = sort;
    let sortDirection = 1;
    if (sortField === field) {
      if (direction === 1) sortDirection = 2;
      else if (direction === 2) sortDirection = 0;
      else if (direction === 0) sortDirection = 1;
    }

    setSort({
      direction: sortDirection,
      field: sortField,
    });
  };

  const getSortedList = () => {
    const { direction, field } = sort;
    let sortedList = [...filteredList];
    sortedList = sortedList.sort((a, b) => {
      const x = String(a[field]).toLowerCase();
      const y = String(b[field]).toLowerCase();

      const desc = direction === 2 ? 1 : -1;
      const asc = direction === 2 ? -1 : 1;

      return x > y ? desc : y > x ? asc : 0;
    });
    return sortedList;
  };

  const fieldShapeDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.field_geo_shape_uuid || "unknown"] =
        e.field_geo_shape_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: fieldId,
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const offloadSiteDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.offload_geo_shape_uuid || "unknown"] =
        e.offload_geo_shape_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: fieldId,
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const storageDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.offload_geo_storage_location_uuid || "unknown"] =
        e.offload_geo_storage_location_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: fieldId,
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const pipeDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.pipe || "Unknown"] = e.pipe || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: uniqueFields[fieldId],
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const targetEgDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.target_equipment_group_uuid || "unknown"] =
        e.target_equipment_group_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: fieldId,
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const sourceEgDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.source_equipment_group_uuid || "unknown"] =
        e.source_equipment_group_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((fieldId) => ({
      id: fieldId,
      label: uniqueFields[fieldId],
    }));
  }, [nonEmptyLoadList]);

  const crewDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.crew_uuid || "unknown"] = e.crew_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((crewId) => ({
      id: crewId,
      label: uniqueFields[crewId],
    }));
  }, [nonEmptyLoadList]);

  const cropDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.crop_name || "unknown"] = e.crop_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((crop_name) => ({
      id: crop_name,
      label: uniqueFields[crop_name],
    }));
  }, [nonEmptyLoadList]);

  const varietyDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.variety || "unknown"] = e.variety || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((variety) => ({
      id: variety,
      label: uniqueFields[variety],
    }));
  }, [nonEmptyLoadList]);

  const vendorDropDownList = useMemo(() => {
    const uniqueFields = (nonEmptyLoadList || []).reduce((dropdownList, e) => {
      dropdownList[e.target_equipment_vendor_id || "unknown"] =
        e.target_equipment_vendor_name || "Unknown";
      return dropdownList;
    }, {});

    return Object.keys(uniqueFields).map((vendorId) => ({
      id: vendorId,
      label: uniqueFields[vendorId],
    }));
  }, [nonEmptyLoadList]);

  const handleBulkSelect = (checked) => {
    handleBulkEditLoadSelect(loadList, checked);
  };

  const handleLoadRestore = (id) => {
    setRestoreConfirm(!restoreConfirm);
    setCurrentLoadActivityId(id);
  };

  const handleLoadRestoreConfirm = () => {
    const restoredDate = moment
      .utc()
      .tz(timezoneIdentifier)
      .format();
    const cb = (isSuccess) => {
      if (isSuccess)
        dispatch(setRestoreLoad(currentLoadActivityId, restoredDate));
    };
    dispatch(
      restoreDeletedLoad(
        {
          load_activity_uuid: currentLoadActivityId,
          restored_date: restoredDate,
        },
        cb
      )
    );
  };

  const getFilterRange = (keyName) => {
    switch (keyName) {
      case "seconds":
        return [
          { value: 60, label: "1m" },
          { value: 600, label: "10m" },
          { value: 3600, label: "1hr" },
        ];
      case "lbs":
        return [
          { value: 25000, label: "25000" },
          { value: 50000, label: "50000" },
          { value: 100000, label: "100000" },
        ];
      case "sacks":
        return [
          { value: 250, label: "250" },
          { value: 500, label: "500" },
          { value: 1000, label: "1000" },
        ];
      case "tons":
      case "tonnes":
        return [
          { value: 15, label: "15" },
          { value: 30, label: "30" },
          { value: 50, label: "50" },
        ];
      case "approve":
        return [
          { value: true, label: "Approved" },
          { value: false, label: "Not Approved" },
        ];
      default:
        return null;
    }
  };

  const getFilterLabelList = useCallback((keyName) => {
    const range = getFilterRange(keyName);
    if (!range) return [];
    if (keyName === "approve")
      return [
        { id: 1, label: range[0].label },
        { id: 2, label: range[1].label },
      ];
    else if (keyName === "seconds")
      return [
        { id: 1, label: `Below ${range[0].label}` },
        { id: 2, label: `${range[0].label} - ${range[1].label}` },
        { id: 3, label: `${range[1].label} - ${range[2].label}` },
        { id: 4, label: `${range[2].label} & Above` },
      ];
    else
      return [
        { id: 1, label: `Below ${range[0].label} (${weightShortName})` },
        {
          id: 2,
          label: `${range[0].label} - ${range[1].label} (${weightShortName})`,
        },
        {
          id: 3,
          label: `${range[1].label} - ${range[2].label} (${weightShortName})`,
        },
        { id: 4, label: `${range[2].label} (${weightShortName}) & Above` },
      ];
  }, []);

  const getDropDownList = (keyName) => {
    const filterObj = {
      crew_name: crewDropDownList,
      field_geo_shape_name: fieldShapeDropDownList,
      crop_name: cropDropDownList,
      variety: varietyDropDownList,
      target_equipment_group_name: targetEgDropDownList,
      source_equipment_group_name: sourceEgDropDownList,
      target_equipment_vendor_name: vendorDropDownList,
      offload_geo_shape_name: offloadSiteDropDownList,
      offload_geo_storage_location_name: storageDropDownList,
      pipe: pipeDropDownList,
      load_gross_weight: getFilterLabelList(weightShortName),
      load_capacity: getFilterLabelList(weightShortName),
      load_seconds: getFilterLabelList("seconds"),
      approved: getFilterLabelList("approve"),
    };
    return filterObj[keyName] ? filterObj[keyName] : [];
  };

  const handleColumnReorder = (result) => {
    if (!result.destination) return;
    let columns =
      columnOrder && columnOrder.length > 0 ? columnOrder : getTableHead;
    columns = getOrderdColums(columns);
    let newColumnOrder = [...columns];
    const [movedColumn] = newColumnOrder.splice(result.source.index, 1);
    newColumnOrder.splice(result.destination.index, 0, movedColumn);
    dispatch(setLoadHistoryHeader(newColumnOrder));
    setColumnOrder(newColumnOrder);
    saveColumnOrder(newColumnOrder);
  };

  const showFilter = nonEmptyLoadList.length <= maxLoadCountForFilter;
  const isAutomaticLoadStatus = isAutomaticLoad(loadType);
  const { header } = initialData;
  const tableHead = getTableHead;

  return (
    <>
      <DragDropContext onDragEnd={handleColumnReorder}>
        <DataTable className="px-0">
          <Droppable droppableId="columns" direction="horizontal">
            {(provided) => (
              <thead ref={provided.innerRef} {...provided.droppableProps}>
                <DataTable.Row>
                  {enableBulkEdit && (
                    <DataTable.Cell
                      width="45px"
                      className={`checkmark_container`}
                    >
                      <SubNav.Checkbox
                        checked={bulkEditLoadList.length > 0}
                        className={`square ${
                          bulkEditLoadList.length > 0 &&
                          bulkEditLoadList.length < filteredList.length
                            ? "partial"
                            : ""
                        }`}
                        onChange={(e) => handleBulkSelect(e.target.checked)}
                      />
                    </DataTable.Cell>
                  )}

                  {tableHead.map((columnKey, index) => {
                    const column = header.find((col) => col.key === columnKey);
                    const isField = columnKey === "field_geo_shape_name";
                    if (!column) {
                      return null; // Handle missing columns
                    }
                    return (
                      <Draggable
                        key={columnKey}
                        draggableId={columnKey}
                        index={index}
                        isDragDisabled={
                          (loadList && loadList.length === 0) ||
                          enableBulkEdit ||
                          showDeletedLoads
                            ? true
                            : false
                        }
                      >
                        {(provided, snapshot) => (
                          <td
                            className={`drag-table-header loadHistory_tableHeader ${
                              isField ? "field_name" : ""
                            }`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            id={columnKey}
                          >
                            <div className="loadHistory_headerData">
                              <span
                                className="sort-column"
                                onClick={() =>
                                  !enableBulkEdit &&
                                  column.isSort &&
                                  handleSort(column.key)
                                }
                              >
                                <span>{column.label}</span>
                                {column.isSort ? (
                                  <span>{renderSortOrder(column.key)}</span>
                                ) : null}
                              </span>
                              {showFilter && column.isPipeFilter && (
                                <FilterDropdown
                                  searchBar={
                                    column.filterSearchBar
                                      ? column.filterSearchBar
                                      : false
                                  }
                                  data={
                                    filterCondition
                                      ? localFilterState[column.filter]
                                      : filter[column.filter]
                                  }
                                  localFilterState={localFilterState}
                                  setLocalFilterState={setLocalFilterState}
                                  nonEmptyLoadList={nonEmptyLoadList}
                                  filterName={column.filter}
                                  dropdownList={getDropDownList(column.key)}
                                  onSelect={(filterName, item, checked) =>
                                    handleFilterSelection(
                                      filterName,
                                      item.id,
                                      item.label,
                                      checked
                                    )
                                  }
                                />
                              )}

                              {snapshot.isDragging && !provided.placeholder && (
                                <div className="ghost-table">
                                  {loadList.map((row, rowIndex) => {
                                    if (columnKey === "action")
                                      return (
                                        <div
                                          key={rowIndex}
                                          className="ghost-tbl-col seed-action d-flex"
                                        >
                                          <div>
                                            <i
                                              aria-hidden="true"
                                              title="Edit"
                                              className="fa fa-pencil ml-2 cursor-pointer"
                                            ></i>
                                          </div>
                                          <div>
                                            <i
                                              aria-hidden="true"
                                              title="Delete"
                                              className="fa fa-trash ml-2 cursor-pointer"
                                            ></i>
                                          </div>
                                        </div>
                                      );
                                    //if (columnKey === "load_action") return <div key={rowIndex} className="ghost-tbl-col seed-action d-flex"><div><div className="load-history-notes"><span><i className="fa fa-plus p-1 cursor-pointer"></i></span></div></div><div><i aria-hidden="true" title="Print" className="fa fa-print ml-2 cursor-pointer"></i></div><div><i aria-hidden="true" title="Location" className="fa fa-map-marker cursor-pointer ml-2"></i></div></div>
                                    if (columnKey === "restore")
                                      return (
                                        <div
                                          key={rowIndex}
                                          className="ghost-tbl-col seed-action d-flex"
                                        >
                                          <div>
                                            <i
                                              aria-hidden="true"
                                              title="Restore"
                                              className="fa fa-undo ml-2 cursor-pointer"
                                            ></i>
                                          </div>
                                        </div>
                                      );
                                    if (columnKey === "approved")
                                      return (
                                        <div
                                          key={rowIndex}
                                          className="ghost-tbl-col seed-action d-flex justify-content-center"
                                        >
                                          <div>
                                            {row[columnKey] &&
                                            row[columnKey] === 1 ? (
                                              <span
                                                title="Approved"
                                                className="badge-sm mr-0 badge badge-pill badge-success"
                                              >
                                                <i className="fa fa-check"></i>
                                              </span>
                                            ) : (
                                              <span
                                                title="Not Approved"
                                                className="badge-sm badge badge-pill badge-danger"
                                              >
                                                <i className="fa fa-times"></i>
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    if (columnKey === "load_date")
                                      return (
                                        <span
                                          key={rowIndex}
                                          className="ghost-tbl-col"
                                        >
                                          {row[columnKey]
                                            ? toDisplayDateTimeString(
                                                row[columnKey],
                                                false,
                                                false
                                              )
                                            : null}
                                        </span>
                                      );
                                    if (columnKey === "load_seconds")
                                      return (
                                        <span
                                          key={rowIndex}
                                          className="ghost-tbl-col"
                                        >
                                          {row[columnKey]
                                            ? toDisplayTimeString(
                                                secondsToTime(
                                                  row[columnKey],
                                                  true
                                                )
                                              )
                                            : null}
                                        </span>
                                      );
                                    if (columnKey === "id")
                                      return (
                                        <span
                                          key={rowIndex}
                                          className="ghost-tbl-col"
                                        >
                                          {rowIndex + 1}
                                        </span>
                                      );
                                    if (columnKey === "events")
                                      return (
                                        <span
                                          key={rowIndex}
                                          className="ghost-tbl-col"
                                        >
                                          -
                                        </span>
                                      );
                                      if (columnKey === "rate_info")
                                        return (
                                          <span
                                            key={rowIndex}
                                            className="ghost-tbl-col"
                                          >
                                            -
                                          </span>
                                        );
                                    else
                                      return (
                                        <span
                                          key={rowIndex}
                                          style={{
                                            color: `${
                                              row[columnKey] &&
                                              row[columnKey] === "Unknown"
                                                ? "red"
                                                : ""
                                            }`,
                                          }}
                                          className="ghost-tbl-col"
                                        >
                                          {row[columnKey]}
                                        </span>
                                      );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </DataTable.Row>
              </thead>
            )}
          </Droppable>
          <DataTable.Body>
            {enableBulkEdit && (
              <LoadBulkEditRow
                loadParams={loadParams}
                showableCustomColumns={tableHead}
                initialHeaders={header}
              />
            )}
            {loadList.map((item) => {
              /*  if (item.transfer_type === 1)  */ serialNumber++;
              return (
                <LoadRow
                  key={item.load_activity_uuid}
                  loadActivityId={item.load_activity_uuid}
                  load={item}
                  isAllowEdit={isAllowEdit}
                  isAllowDelete={isAllowDelete}
                  isAllowCreate={isAllowCreate}
                  serialNumber={serialNumber}
                  farmId={farmId}
                  showableCustomColumns={tableHead}
                  isAutomaticLoad={isAutomaticLoadStatus}
                  handleLoadRestore={handleLoadRestore}
                  loadParams={loadParams}
                  initialHeaders={header}
                />
              );
            })}
          </DataTable.Body>
        </DataTable>
      </DragDropContext>
      <Modal.Confirm
        className="modal-bordered"
        show={restoreConfirm}
        onConfirm={handleLoadRestoreConfirm}
        onHide={() => setRestoreConfirm(false)}
        iconClassName="fa fa-question"
        title={{ content: "Confirmation" }}
        content={`Do you want to restore?`}
      />
    </>
  );
};
export default LoadHistoryTable;